import { Card } from "@mui/material";
import ExpandableCard from "src/components/common/ExpandableCard";
import { WrappedPatient } from "src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient";
import { PatientEngagementList } from "./PatientEngagementList";
import useLocales from "src/careflow/ph-core/internationalization/hook/useLocales";
import { checkAclValidation } from "src/careflow/ph-core/admin/permissions/util/permission.utils";
import crsAcls from "src/careflow/ph-core/admin/permissions/util/crs/crsAcls";
import { useMemo } from "react";
import PatientTasks from "src/careflow/care-coordination/patient/PatientTasks";
import { useQuery } from "@tanstack/react-query";
import { getRegistries } from "src/careflow/ph-core/multi-tenant/registry/registry";
import PatientGenericServiceRequest, { ServiceRequestsPermissions } from "src/careflow/care-coordination/patient/service-request/PatientGenericServiceRequest";

type Props = {
  patient: WrappedPatient;
};

export default function EngagementHubTab({ patient }: Props) {
  const { i18n } = useLocales();

  const serviceRequestPermissions: ServiceRequestsPermissions = useMemo(
    () => ({
      isAllowedToAdd: checkAclValidation({ acls: [crsAcls.CRS.PATIENT.SERVICE_REQUESTS.ADD] }),
      isAllowedToEdit: checkAclValidation({ acls: [crsAcls.CRS.PATIENT.SERVICE_REQUESTS.EDIT] }),
      isAllowedToViewNotes: checkAclValidation({
        acls: [crsAcls.CRS.PATIENT.SERVICE_REQUESTS.NOTES.VIEW],
      }),
      taskPermissions: {
        isAllowedToAdd: checkAclValidation({
          acls: [crsAcls.CRS.PATIENT.SERVICE_REQUESTS.TASKS.ADD],
        }),
        isAllowedToEdit: checkAclValidation({
          acls: [crsAcls.CRS.PATIENT.SERVICE_REQUESTS.TASKS.EDIT],
        }),
        notes: {
          isAllowedToView: checkAclValidation({
            acls: [crsAcls.CRS.PATIENT.SERVICE_REQUESTS.TASKS.NOTES.VIEW],
          }),
          isAllowedToAdd: checkAclValidation({
            acls: [crsAcls.CRS.PATIENT.SERVICE_REQUESTS.TASKS.NOTES.ADD],
          }),
        },
        isAllowedToAssignProgram: checkAclValidation({
          acls: [crsAcls.CRS.PATIENT.SERVICE_REQUESTS.TASKS.HANDLER.ASSIGN_PROGRAM],
        }),
        isAllowedReferralInternal: checkAclValidation({
          acls: [crsAcls.CRS.PATIENT.SERVICE_REQUESTS.TASKS.HANDLER.REFERRAL_INTERNAL],
        }),
        isAllowedReferralExternal:checkAclValidation({
          acls: [crsAcls.CRS.PATIENT.SERVICE_REQUESTS.TASKS.HANDLER.REFERRAL_EXTERNAL],
        }),
        isAllowedAppointmentScheduling: checkAclValidation({
          acls: [crsAcls.CRS.PATIENT.SERVICE_REQUESTS.TASKS.HANDLER.APPOINTMENT],
        }),
        isAllowedWarmHandoff: checkAclValidation({
          acls: [crsAcls.CRS.PATIENT.SERVICE_REQUESTS.TASKS.HANDLER.WARM_HANDOFF],
        }),
      }
    }),
    []
  );

  const { data: taskADRegistry } = useQuery({
    queryKey: ['taskActivityDefinition'],
    queryFn: async () => {
      const taskActivityDefinition = await getRegistries(undefined, 'task-activity-definition');
      return taskActivityDefinition;
    },
  });

  const taskActivityDefinition = useMemo(() => 
    taskADRegistry?.[0]?.keyValue ? JSON.parse(taskADRegistry?.[0]?.keyValue) : null
    ,[taskADRegistry]);

  return (
    <Card sx={{ py: 2, boxShadow: 'none' }}>
      <ExpandableCard 
        title={i18n('patients.details.engagement.title', 'crs')} 
        sxProp={{ m: 1 }} 
        openCollapseExternal={true}
      >
        <PatientEngagementList patient={patient} />
      </ExpandableCard>

      <ExpandableCard 
        title={i18n('nat.request.title', 'translations')} 
        sxProp={{ m: 1 }} 
        openCollapseExternal={true}
      >
        <PatientGenericServiceRequest 
          patient={patient}
          permissions={serviceRequestPermissions}
          taskActivityDefinitionContext={taskActivityDefinition?.patientServiceRequest}
        />
      </ExpandableCard>

      <ExpandableCard 
        title={i18n('nat.tabTitle', 'translations')} 
        sxProp={{ m: 1 }} 
        openCollapseExternal={true}
      >
        <PatientTasks patient={patient} includeSubTasks={false} />
      </ExpandableCard>
    </Card>
  );
}
